import { useEffect, useRef } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

interface UseVideoPreloaderProps {
  videos: MediaItem[];
  currentIndex: number;
  enabled: boolean;
}

export const useVideoPreloader = ({
  videos,
  currentIndex,
  enabled,
}: UseVideoPreloaderProps) => {
  const preloadRef1 = useRef<HTMLVideoElement | null>(null);
  const preloadRef2 = useRef<HTMLVideoElement | null>(null);
  const preloadedIndices = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled || videos.length === 0) return;

    // Calculate which videos should be preloaded (next 2)
    const targetIndices: number[] = [];

    if (currentIndex + 1 < videos.length) {
      targetIndices.push(currentIndex + 1);
    }

    if (currentIndex + 2 < videos.length) {
      targetIndices.push(currentIndex + 2);
    }

    // Clean up videos that are no longer in the preload window
    preloadedIndices.current.forEach((index) => {
      if (!targetIndices.includes(index)) {
        preloadedIndices.current.delete(index);
        console.log(`🧹 Cleaning up preload for video at index ${index}`);
      }
    });

    // Clear video elements that are not in use
    const activeElements = new Set<HTMLVideoElement>();

    // Preload new videos
    targetIndices.forEach((index, i) => {
      const videoElement = i === 0 ? preloadRef1.current : preloadRef2.current;
      if (!videoElement) return;

      activeElements.add(videoElement);

      // Skip if already preloaded at this index
      if (preloadedIndices.current.has(index)) {
        return;
      }

      const video = videos[index];
      if (!video) return;

      const streamUrl = video.getStreamUrlWithQueryAuth(true); // skipHeal=true for fast preloading
      if (!streamUrl) return;

      // Clear previous src and set new one
      videoElement.src = "";
      videoElement.src = streamUrl;
      videoElement.preload = "auto";
      preloadedIndices.current.add(index);

      console.log(`📥 Preloading video at index ${index}:`, video.originalFilename);
    });

    // Clear any video elements not being used
    [preloadRef1.current, preloadRef2.current].forEach((element) => {
      if (element && !activeElements.has(element) && element.src) {
        element.src = "";
        console.log(`🧹 Cleared unused video element`);
      }
    });
  }, [videos, currentIndex, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preloadRef1.current) {
        preloadRef1.current.src = "";
      }
      if (preloadRef2.current) {
        preloadRef2.current.src = "";
      }
      preloadedIndices.current.clear();
    };
  }, []);

  return { preloadRef1, preloadRef2 };
};
